import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "../../ui/alert-dialog";

export interface AppAlertDialogProps {
	/**
	 * Element that opens the dialog (e.g. a Button). Omit when controlling
	 * the dialog yourself via `open` / `onOpenChange`.
	 */
	trigger?: React.ReactNode;

	/** Controlled open state. Leave undefined to let the dialog manage itself. */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;

	/** i18n key or literal — translated automatically. */
	title: string;
	/** i18n key or literal — translated automatically. Optional. */
	description?: string;

	/** Confirm button label (i18n key). Defaults to `YES`. */
	confirmLabel?: string;
	/** Cancel button label (i18n key). Defaults to `CANCEL`. */
	cancelLabel?: string;

	/**
	 * Called when the user confirms. If it returns a promise the dialog stays
	 * open and shows the confirm button as disabled until it resolves, then closes.
	 */
	onConfirm?: () => void | Promise<void>;
	/** Called when the user cancels / dismisses. */
	onCancel?: () => void;

	/** `danger` renders the confirm button with the `danger` token (destructive actions). */
	variant?: "default" | "danger";

	/** Force the confirm button into a disabled/loading state. */
	loading?: boolean;
	/** Disable the confirm button (e.g. pending validation). */
	confirmDisabled?: boolean;

	/** Skip translation of title/description/labels (rare). */
	skipTranslation?: boolean;

	/** Optional extra rich content rendered between the description and the footer. */
	children?: React.ReactNode;

	contentClassName?: string;
	confirmClassName?: string;
	cancelClassName?: string;
}

/**
 * Reusable confirmation dialog built on the shadcn/Radix `alert-dialog`
 * primitive (`components/ui/alert-dialog`). Use for "are you sure?" style
 * blocking confirmations. For the legacy global modal flow see
 * `Shared/ConfirmationModal` (modalStore) — prefer this for new, local dialogs.
 */
const AppAlertDialog: React.FC<AppAlertDialogProps> = ({
	trigger,
	open,
	onOpenChange,
	title,
	description,
	confirmLabel = "YES",
	cancelLabel = "CANCEL",
	onConfirm,
	onCancel,
	variant = "default",
	loading = false,
	confirmDisabled = false,
	skipTranslation = false,
	children,
	contentClassName,
	confirmClassName,
	cancelClassName,
}) => {
	const { t } = useTranslation();
	const tr = (key?: string) => (key ? (skipTranslation ? key : t(key as any)) : "");

	const isControlled = open !== undefined;
	const [internalOpen, setInternalOpen] = useState(false);
	const [internalLoading, setInternalLoading] = useState(false);
	const actualOpen = isControlled ? open : internalOpen;

	const setOpen = (next: boolean) => {
		onOpenChange?.(next);
		if (!isControlled) setInternalOpen(next);
	};

	const busy = loading || internalLoading;

	async function handleConfirm(e: React.MouseEvent<HTMLButtonElement>) {
		if (!onConfirm) return; // let Radix auto-close
		const result = onConfirm();
		if (result instanceof Promise) {
			// Keep the dialog open while the async action runs.
			e.preventDefault();
			try {
				setInternalLoading(true);
				await result;
				setOpen(false);
			} finally {
				setInternalLoading(false);
			}
		}
	}

	return (
		<AlertDialog open={actualOpen} onOpenChange={setOpen}>
			{trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
			<AlertDialogContent className={contentClassName}>
				<AlertDialogHeader>
					<AlertDialogTitle>{tr(title)}</AlertDialogTitle>
					{description && <AlertDialogDescription>{tr(description)}</AlertDialogDescription>}
				</AlertDialogHeader>

				{children}

				<AlertDialogFooter>
					<AlertDialogCancel
						className={cancelClassName}
						disabled={busy}
						onClick={() => onCancel?.()}
					>
						{tr(cancelLabel)}
					</AlertDialogCancel>
					<AlertDialogAction
						className={cn(
							// Default confirm inherits the brand-green `primary` from the
							// alert-dialog primitive; danger swaps in the shared danger token.
							variant === "danger" && "bg-danger text-white hover:bg-danger/90",
							confirmClassName
						)}
						disabled={busy || confirmDisabled}
						onClick={handleConfirm}
					>
						{tr(confirmLabel)}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};

export default AppAlertDialog;
